import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PredefinedDataCustomInputRow from 'components/inputs/predefined-data-custom-input/PredefinedDataCustomInputRow';
import { Group, Skeleton, Stack } from '@mantine/core';
import Button from 'components/Button';
import { IconPlus } from '@tabler/icons-react';
import { nanoid } from 'nanoid';
import {
  RevisionPredefinedDataOption,
  RevisionPredefinedDataSource,
} from 'components/inputs/revision-predefined-data/types';
import { useRevisionPredefinedDataCatalog } from 'components/inputs/revision-predefined-data/useRevisionPredefinedDataCatalog';

export interface PredefinedDataTriplet {
  name: string; // = value
  description?: string;
  moreInfo?: string;
  id?: string;
  source?: RevisionPredefinedDataSource;
}

interface PredefinedDataCustomInputProps {
  deviceTypeId: number;
  deviceTypeName: string;
  predefinedDataType: string | number;
  revisionId: number;
  withMoreInfo?: boolean;
  moreInfoMultiline?: boolean;
  allowDuplicates?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  value?: PredefinedDataTriplet[];
  onChange?: (value: PredefinedDataTriplet[]) => void;
}

/**
 * Custom input for predefined data in revisions.
 */
export default function PredefinedDataCustomInput({
  deviceTypeId,
  deviceTypeName,
  predefinedDataType,
  revisionId,
  withMoreInfo = false,
  moreInfoMultiline = false,
  allowDuplicates = false,
  size = 'lg',
  value = [],
  onChange = noop,
}: PredefinedDataCustomInputProps) {
  const [autoFocus, setAutoFocus] = useState<number | null>(null);
  const catalog = useRevisionPredefinedDataCatalog({
    deviceTypeId,
    predefinedDataType,
    revisionId,
  });

  const optionsByName = useMemo(
    () => new Map(catalog.options.map((option) => [option.value, option])),
    [catalog.options]
  );

  const excludeNames = useMemo(
    () => new Set(allowDuplicates ? [] : value.map(({ name }) => name)),
    [allowDuplicates, value]
  );

  const availableOptions = useMemo(
    () => catalog.options.filter(({ value }) => !excludeNames.has(value)),
    [catalog.options, excludeNames]
  );

  // Set IDs for the rows if they are not set.
  useEffect(() => {
    if (value.some(({ id }) => !id)) {
      onChange(value.map((item) => ({ ...item, id: item.id ?? nanoid() })));
    }
  }, [value]);

  // Reset the autofocus.
  useEffect(() => {
    setAutoFocus(null);
  }, [value]);

  /**
   * Adds a new row to the list.
   */
  const addRow = useCallback(() => {
    onChange([...value, { id: nanoid(), name: '', description: '', moreInfo: '' }]);
    setAutoFocus(value.length);
  }, [value]);

  return (
    <>
      {catalog.loading ? (
        <Stack spacing={16}>
          <Skeleton w="full" h={48} radius={4} animate />
          <Skeleton w="full" h={48} radius={4} animate />
          <Skeleton w="full" h={48} radius={4} animate />
        </Stack>
      ) : (
        <Stack>
          {value
            .filter(({ id }) => !!id)
            .map((item, index) => {
              return (
                <PredefinedDataCustomInputRow
                  key={item.id}
                  value={item}
                  options={
                    allowDuplicates
                      ? catalog.options
                      : ([...availableOptions, optionsByName.get(item.name)].filter(
                          Boolean
                        ) as RevisionPredefinedDataOption[])
                  }
                  optionExists={item.name === '' || optionsByName.has(item.name)}
                  reservedNames={
                    allowDuplicates
                      ? []
                      : value
                          .filter((row) => row.id !== item.id)
                          .map((row) => row.name)
                          .filter(Boolean)
                  }
                  deviceTypeName={deviceTypeName}
                  predefinedDataTypeId={Number(predefinedDataType)}
                  predefinedDataTypeName={catalog.predefinedDataTypeName}
                  moreInfoMultiline={moreInfoMultiline}
                  withMoreInfo={withMoreInfo}
                  autoFocus={autoFocus === index}
                  loading={catalog.loading}
                  onChange={(newItem) =>
                    onChange(value.map((oldItem) => (oldItem.id === newItem.id ? newItem : oldItem)))
                  }
                  onRowDelete={() => onChange(value.filter((oldItem) => oldItem.id !== item.id))}
                  onCatalogOptionCreate={catalog.upsertCatalogOption}
                  size={size}
                />
              );
            })}
          <Group>
            <Button variant="subtle" leftIcon={<IconPlus />} onClick={addRow}>
              Pridať nový riadok
            </Button>
          </Group>
        </Stack>
      )}
    </>
  );
}
